/**
 * News Data
 *
 * Static news items and category configuration for the news/announcements feature
 * Based on specification from requirements/news/news.txt
 *
 * @module newsData
 */

import type { NewsItem, NewsCategoryConfigMap, NewsCategory } from './newsTypes';
import { CENY_PRADU_2026_CONTENT } from './content/ceny-pradu-2026-5-rzeczy';
import { KALKULATOR_AKTUALIZACJA_GRUDZIEN_2025_CONTENT } from './content/kalkulator-aktualizacja-grudzien-2025';
import {
  TAURON_OSD_LUTY_2026_CONTENT,
  PGE_OSD_LUTY_2026_CONTENT,
  ENEA_OSD_LUTY_2026_CONTENT,
  ENERGA_OSD_LUTY_2026_CONTENT,
} from './content/osd-luty-2026';
import { OGRZEWANIE_ELEKTRYCZNE_TAURON_2026_CONTENT } from './content/ogrzewanie-elektryczne-tauron-2026';

/**
 * News category configuration
 * Defines display properties for each news category
 */
export const NEWS_CATEGORIES: NewsCategoryConfigMap = {
  feature: {
    label: 'Nowość',
    color: 'green',
    icon: 'IconSparkles',
    description: 'Nowe funkcje i możliwości platformy',
  },
  update: {
    label: 'Aktualizacja',
    color: 'blue',
    icon: 'IconRefresh',
    description: 'Ulepszenia istniejących funkcji',
  },
  announcement: {
    label: 'Ogłoszenie',
    color: 'yellow',
    icon: 'IconBell',
    description: 'Ważne informacje',
  },
  maintenance: {
    label: 'Konserwacja',
    color: 'red',
    icon: 'IconTool',
    description: 'Prace techniczne',
  },
};

/**
 * All news items (sorted by date, newest first)
 */
export const NEWS_ITEMS: NewsItem[] = [
  // News #16: Ogrzewanie elektryczne TAURON 2026
  {
    id: 'ogrzewanie-elektryczne-tauron-2026',
    slug: 'ogrzewanie-elektryczne-tauron-2026',
    title: 'Ogrzewanie elektryczne TAURON 2026 - Porownanie taryf',
    category: 'announcement',
    date: '2026-02-02T10:00:00Z',
    featured: true,
    icon: 'IconFlame',
    tags: ['TAURON', 'ogrzewanie', 'pompa ciepla', 'piece akumulacyjne', 'G12as', 'G13', 'G13s', 'G14', '2026'],
    author: 'Zespol TaryfyPradu.pl',
    readTime: '6 min',
    excerpt:
      'Planujesz ogrzewanie elektryczne? Piece akumulacyjne, pompa ciepla, czy system hybrydowy? Sprawdz, ktora taryfa TAURON da Ci najwieksze oszczednosci - nawet do 40%!',
    content: OGRZEWANIE_ELEKTRYCZNE_TAURON_2026_CONTENT,
    osd: ['TAURON'],
    ctaButtons: [
      {
        text: 'Oblicz oszczednosci',
        url: '/TAURON/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'orange',
        icon: 'IconCalculator',
      },
      {
        text: 'Przewodnik po taryfach',
        url: '/TAURON/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconBook',
      },
    ],
  },

  // News #15: ENERGA OSD - Luty 2026
  {
    id: 'energa-osd-luty-2026',
    slug: 'energa-osd-luty-2026',
    title: 'ENERGA - Twój kalkulator taryf na Północy!',
    category: 'feature',
    date: '2026-02-01T12:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['ENERGA', 'OSD', 'taryfy', '2026', 'G12r', 'północ'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt:
      'ENERGA-Operator z pełnym wsparciem! Sprawdź wszystkie taryfy dostępne na północy Polski, w tym unikalną taryfę G12r z rozszerzoną strefą nocną.',
    content: ENERGA_OSD_LUTY_2026_CONTENT,
    osd: ['ENERGA'],
    ctaButtons: [
      {
        text: 'Porównaj taryfy ENERGA',
        url: '/ENERGA/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Przewodnik po taryfach',
        url: '/ENERGA/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconBook',
      },
    ],
  },

  // News #14: ENEA OSD - Luty 2026
  {
    id: 'enea-osd-luty-2026',
    slug: 'enea-osd-luty-2026',
    title: 'ENEA - Pełna obsługa w kalkulatorze TaryfyPrądu.pl!',
    category: 'feature',
    date: '2026-02-01T11:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['ENEA', 'OSD', 'taryfy', '2026', 'G12as', 'zachód'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt:
      'Klienci ENEA Operator mogą teraz porównać wszystkie dostępne taryfy! Sprawdź G12as dla pomp ciepła i znajdź najlepszą opcję dla siebie.',
    content: ENEA_OSD_LUTY_2026_CONTENT,
    osd: ['ENEA'],
    ctaButtons: [
      {
        text: 'Porównaj taryfy ENEA',
        url: '/ENEA/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Przewodnik po taryfach',
        url: '/ENEA/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconBook',
      },
    ],
  },

  // News #13: PGE OSD - Luty 2026
  {
    id: 'pge-osd-luty-2026',
    slug: 'pge-osd-luty-2026',
    title: 'PGE - Teraz z pełnym wsparciem w kalkulatorze!',
    category: 'feature',
    date: '2026-02-01T10:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['PGE', 'OSD', 'taryfy', '2026', 'G12n', 'centrum'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt:
      'Największy dystrybutor w Polsce teraz z pełnym wsparciem! Sprawdź wszystkie taryfy PGE, w tym unikalną taryfę G12n.',
    content: PGE_OSD_LUTY_2026_CONTENT,
    osd: ['PGE'],
    ctaButtons: [
      {
        text: 'Porównaj taryfy PGE',
        url: '/PGE/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Przewodnik po taryfach',
        url: '/PGE/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconBook',
      },
    ],
  },

  // News #12: TAURON OSD - Luty 2026
  {
    id: 'tauron-osd-luty-2026',
    slug: 'tauron-osd-luty-2026',
    title: 'TAURON - Pełna obsługa wszystkich taryf 2026',
    category: 'feature',
    date: '2026-02-01T09:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['TAURON', 'OSD', 'taryfy', '2026', 'G14', 'G13', 'południe'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '4 min',
    excerpt:
      'TAURON z najbogatszą ofertą taryf w Polsce! Od G11 po dynamiczną G14 - sprawdź wszystkie opcje i funkcje dostępne tylko dla klientów TAURON.',
    content: TAURON_OSD_LUTY_2026_CONTENT,
    osd: ['TAURON'],
    ctaButtons: [
      {
        text: 'Porównaj taryfy TAURON',
        url: '/TAURON/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Kalkulator Profesjonalny',
        url: '/TAURON/kalkulatory/zaawansowany',
        variant: 'outline',
        color: 'blue',
        icon: 'IconChartLine',
      },
    ],
  },

  // News #11: Kalkulator - Aktualizacja Styczeń 2026 (FEATURED, 2026-01-08)
  {
    id: 'kalkulator-aktualizacja-styczen-2026',
    slug: 'kalkulator-aktualizacja-styczen-2026',
    title: 'Kalkulator Styczeń 2026 - Obsługa wszystkich dystrybutorów w Polsce!',
    category: 'feature',
    date: '2026-01-08T10:00:00Z',
    featured: true,
    icon: 'IconMap',
    tags: ['OSD', 'PGE', 'Enea', 'Energa', 'Tauron', 'multi-OSD', '2026'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt:
      'Wielka aktualizacja! Teraz porównasz taryfy nie tylko Tauron, ale również PGE, Enea i Energa. Obsługujemy ponad 17 milionów klientów w całej Polsce!',
    content: `# Kalkulator Styczeń 2026 - Obsługa wszystkich dystrybutorów w Polsce!

Mamy świetną wiadomość dla użytkowników spoza regionu Tauron! Od teraz **TaryfyPrądu.pl obsługuje wszystkich 4 głównych dystrybutorów energii w Polsce**.

## 🗺️ Obsługiwani dystrybutorzy (OSD)

| Dystrybutor | Region | Klienci | Dostępne taryfy |
|-------------|--------|---------|-----------------|
| **TAURON®** | Południowa Polska | 5,7 mln | G11, G12, G12w, G12as, G13, G13s, G14 |
| **PGE®** | Centralna/Wschodnia | 5,5 mln | G11, G12, G12w |
| **ENEA®** | Zachodnia Polska | 2,6 mln | G11, G12, G12w, G12as |
| **ENERGA®** | Północna Polska | 3,2 mln | G11, G12, G12w, G12as |

**Razem obsługujemy ponad 17 milionów klientów!**

## 🎯 Jak wybrać dystrybutora?

### W Kalkulatorze Podstawowym
1. Przejdź do [Kalkulatora Podstawowego](/kalkulatory/podstawowy)
2. Na górze strony znajdziesz **selektor dystrybutora**
3. Wybierz swojego OSD (sprawdź na fakturze lub po regionie)
4. Taryfy i ceny automatycznie dostosują się do Twojego dystrybutora

### W Kalkulatorze Zaawansowanym
1. Przejdź do [Kalkulatora Zaawansowanego](/kalkulatory/zaawansowany)
2. W sekcji parametrów wybierz **dystrybutora**
3. Dostępne taryfy zmienią się w zależności od OSD

## 📊 Nowe funkcje w tej aktualizacji

### 1. Selektor roku kalkulacji
Teraz możesz **porównać koszty dla 2025 vs 2026**:
- Zobacz jak wzrosną Twoje rachunki
- Sprawdź różnicę między starymi a nowymi stawkami
- Zaplanuj budżet domowy na 2026

### 2. Podgląd CSV przed importem
Przed zaimportowaniem danych godzinowych z eLicznika:
- **Podgląd pierwszych wierszy** pliku
- **Walidacja formatu** przed importem
- **Informacja o wykrytych błędach**

### 3. Uproszczona nawigacja
Przeprojektowaliśmy menu główne:
- **Jeden poziom nawigacji** zamiast rozwijanego menu
- **Wyraźny przycisk CTA** do kalkulatora
- **Szybszy dostęp** do najważniejszych funkcji

## ❓ Który dystrybutor obsługuje mój region?

### TAURON Dystrybucja
- dolnośląskie, małopolskie, opolskie, śląskie
- część świętokrzyskiego, podkarpackiego, łódzkiego

### PGE Dystrybucja
- mazowieckie, łódzkie, lubelskie, podlaskie
- część podkarpackiego, świętokrzyskiego

### ENEA Operator
- wielkopolskie, zachodniopomorskie, kujawsko-pomorskie, lubuskie
- część dolnośląskiego, pomorskiego

### ENERGA-Operator
- pomorskie, warmińsko-mazurskie
- część kujawsko-pomorskiego, mazowieckiego, wielkopolskiego

**Nie wiesz który masz?** Sprawdź na swojej fakturze - nazwa dystrybutora jest zawsze podana.

## 🔮 Co planujemy dalej?

- **Ceny 2026 dla wszystkich OSD** - aktualnie mamy pełne dane dla Tauron
- **Import danych z innych liczników** - nie tylko Tauron eLicznik
- **Porównanie ofert sprzedawców** - różne firmy sprzedające energię

---

**Chcesz sprawdzić swoje oszczędności?** [Wypróbuj kalkulator](/kalkulatory/podstawowy) z nowym selektorem dystrybutora!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Wypróbuj kalkulator',
        url: '/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Porównaj taryfy',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #10: Kalkulator Zaawansowany - Aktualizacja Grudzień 2025 (FEATURED, 2025-12-19)
  {
    id: 'kalkulator-aktualizacja-grudzien-2025',
    slug: 'kalkulator-aktualizacja-grudzien-2025',
    title: 'Kalkulator Zaawansowany - Aktualizacja Grudzień 2025',
    category: 'update',
    date: '2025-12-19T15:00:00Z',
    featured: true,
    icon: 'IconChartLine',
    tags: ['kalkulator', 'aktualizacja', '2026', 'FROM_CSV_IMPORT', 'UX', 'nowe funkcje'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '4 min',
    excerpt:
      'Obsługa cen 2026, import danych z Tauron, nowy widok miesięczny i wiele usprawnień UX. Zobacz co nowego w Kalkulatorze Zaawansowanym!',
    content: KALKULATOR_AKTUALIZACJA_GRUDZIEN_2025_CONTENT,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Wypróbuj kalkulator',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'green',
        icon: 'IconChartLine',
      },
      {
        text: 'Porównaj taryfy 2026',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #9: Ceny prądu 2026 - 5 rzeczy które musisz wiedzieć (FEATURED, 2025-12-19)
  {
    id: 'ceny-pradu-2026-5-rzeczy',
    slug: 'ceny-pradu-2026-5-rzeczy',
    title: 'Ceny prądu 2026: 5 rzeczy które musisz wiedzieć',
    category: 'announcement',
    date: '2025-12-19T14:00:00Z',
    featured: true,
    icon: 'IconBulb',
    tags: ['ceny prądu', '2026', 'oszczędności', 'taryfy', 'poradnik', 'rachunki'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '4 min',
    excerpt:
      'Ile więcej zapłacisz za prąd w 2026? Jak możesz zaoszczędzić nawet 25%? Prosty przewodnik po zmianach - bez skomplikowanych tabel i branżowego żargonu.',
    content: CENY_PRADU_2026_CONTENT,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Oblicz swój rachunek 2026',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'blue',
        icon: 'IconCalculator',
      },
      {
        text: 'Porównaj taryfy',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #8a: Zmiany taryf energetycznych 2026 - TAURON (FEATURED, 2025-12-19)
  {
    id: 'zmiany-taryf-2026-tauron',
    slug: 'zmiany-taryf-2026-tauron',
    title: 'Taryfy TAURON 2026 - Co się zmienia od 1 stycznia?',
    category: 'announcement',
    date: '2025-12-19T10:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['taryfy', '2026', 'URE', 'opłaty', 'energia', 'zmiany', 'TAURON'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '5 min',
    excerpt: 'Od 1 stycznia 2026 wchodzą w życie nowe taryfy URE dla TAURON. Sprawdź aktualne ceny energii dla swojego dystrybutora!',
    content: `# Taryfy TAURON 2026 - Co się zmienia od 1 stycznia?

Od **1 stycznia 2026** wchodzą w życie nowe taryfy zatwierdzone przez Urząd Regulacji Energetyki (URE) dla **TAURON Dystrybucja**.

## ⚠️ Cel edukacyjny

**Kalkulator służy wyłącznie celom edukacyjnym.** Przed zmianą taryfy skonsultuj się z TAURON.

## 📊 Ceny TAURON 2026 (brutto)

| Taryfa | Cena | Szczegóły |
|--------|------|-----------|
| **G11** | 0,89 zł/kWh | Jednostrefowa |
| **G12** | Dzień: 1,02 zł · Noc: 0,52 zł | Dwustrefowa (06-22/22-06) |
| **G12w** | Robocze: 1,00 zł · Weekend: 0,58 zł | Weekendowa |
| **G12as** | Dzień: 0,90 zł · Noc: 0,58 zł | Antysmogowa |
| **G13** | Szczyt: 1,01 zł · Poza: 0,58 zł | Trzystrefowa |
| **G13s** | Solar: 0,58 zł · Inne: 0,92 zł | Dynamiczna OZE |
| **G14** | Zmienna (~0,90 zł średnio) | Dynamiczna RCE |

## 💪 Opłata mocowa 2026

| Roczne zużycie | 2026 netto |
|----------------|------------|
| < 500 kWh | 4,29 zł/mies. |
| 500-1200 kWh | 10,31 zł/mies. |
| 1200-2800 kWh | 17,18 zł/mies. |
| > 2800 kWh | 24,05 zł/mies. |

## 💡 Jak oszczędzać z TAURON?

- **G12:** Przesuń pranie/zmywanie na noc (0,52 zł vs 1,02 zł!)
- **G12as:** Idealna dla pomp ciepła (tylko TAURON!)
- **G13/G13s:** Dla prosumentów z fotowoltaiką

---

**Oblicz swój rachunek!** [Kalkulator TAURON](/kalkulatory/zaawansowany)`,
    osd: ['TAURON'],
    ctaButtons: [
      {
        text: 'Kalkulator TAURON',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'blue',
        icon: 'IconChartBar',
      },
      {
        text: 'Szczegóły taryf',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #8b: Zmiany taryf energetycznych 2026 - PGE (FEATURED, 2025-12-19)
  {
    id: 'zmiany-taryf-2026-pge',
    slug: 'zmiany-taryf-2026-pge',
    title: 'Taryfy PGE 2026 - Co się zmienia od 1 stycznia?',
    category: 'announcement',
    date: '2025-12-19T10:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['taryfy', '2026', 'URE', 'opłaty', 'energia', 'zmiany', 'PGE'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '5 min',
    excerpt: 'Od 1 stycznia 2026 wchodzą w życie nowe taryfy URE dla PGE. Sprawdź aktualne ceny energii dla swojego dystrybutora!',
    content: `# Taryfy PGE 2026 - Co się zmienia od 1 stycznia?

Od **1 stycznia 2026** wchodzą w życie nowe taryfy zatwierdzone przez Urząd Regulacji Energetyki (URE) dla **PGE Dystrybucja**.

## ⚠️ Cel edukacyjny

**Kalkulator służy wyłącznie celom edukacyjnym.** Przed zmianą taryfy skonsultuj się z PGE.

## 📊 Ceny PGE 2026 (brutto)

| Taryfa | Cena | Szczegóły |
|--------|------|-----------|
| **G11** | 1,00 zł/kWh | Jednostrefowa |
| **G12** | Dzień: 1,04 zł · Noc: 0,59 zł | Dwustrefowa (06-22/22-06) |
| **G12w** | Robocze: 1,03 zł · Weekend: 0,78 zł | Weekendowa |
| **G12n** | Dzień: 1,04 zł · Noc: 0,62 zł | Nocna PGE (01-05 + weekendy) |

*G12n to specyficzna taryfa PGE z krótką strefą nocną 01:00-05:00 + całe weekendy*

## 💪 Opłata mocowa 2026

| Roczne zużycie | 2026 netto |
|----------------|------------|
| < 500 kWh | 4,29 zł/mies. |
| 500-1200 kWh | 10,31 zł/mies. |
| 1200-2800 kWh | 17,18 zł/mies. |
| > 2800 kWh | 24,05 zł/mies. |

## 💡 Jak oszczędzać z PGE?

- **G12:** Noc tańsza o 43% (0,59 vs 1,04 zł)
- **G12n:** Idealna dla ładowania EV w nocy (01:00-05:00)
- **G12w:** Weekend całodobowo tańszy

---

**Oblicz swój rachunek!** [Kalkulator PGE](/kalkulatory/zaawansowany)`,
    osd: ['PGE'],
    ctaButtons: [
      {
        text: 'Kalkulator PGE',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'blue',
        icon: 'IconChartBar',
      },
      {
        text: 'Szczegóły taryf',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #8c: Zmiany taryf energetycznych 2026 - ENEA (FEATURED, 2025-12-19)
  {
    id: 'zmiany-taryf-2026-enea',
    slug: 'zmiany-taryf-2026-enea',
    title: 'Taryfy ENEA 2026 - Co się zmienia od 1 stycznia?',
    category: 'announcement',
    date: '2025-12-19T10:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['taryfy', '2026', 'URE', 'opłaty', 'energia', 'zmiany', 'ENEA'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '5 min',
    excerpt: 'Od 1 stycznia 2026 wchodzą w życie nowe taryfy URE dla ENEA. Sprawdź aktualne ceny energii dla swojego dystrybutora!',
    content: `# Taryfy ENEA 2026 - Co się zmienia od 1 stycznia?

Od **1 stycznia 2026** wchodzą w życie nowe taryfy zatwierdzone przez Urząd Regulacji Energetyki (URE) dla **ENEA Operator**.

## ⚠️ Cel edukacyjny

**Kalkulator służy wyłącznie celom edukacyjnym.** Przed zmianą taryfy skonsultuj się z ENEA.

## 📊 Ceny ENEA 2026 (brutto)

| Taryfa | Cena | Szczegóły |
|--------|------|-----------|
| **G11** | 0,99 zł/kWh | Jednostrefowa |
| **G12** | Dzień: 1,03 zł · Noc: 0,58 zł | Dwustrefowa (06-22/22-06) |
| **G12w** | Robocze: 1,02 zł · Weekend: 0,78 zł | Weekendowa |
| **G12as** | Dzień: 1,03 zł · Noc: 0,76 zł | Antysmogowa |

## 💪 Opłata mocowa 2026

| Roczne zużycie | 2026 netto |
|----------------|------------|
| < 500 kWh | 4,29 zł/mies. |
| 500-1200 kWh | 10,31 zł/mies. |
| 1200-2800 kWh | 17,18 zł/mies. |
| > 2800 kWh | 24,05 zł/mies. |

## 💡 Jak oszczędzać z ENEA?

- **G12:** Noc tańsza o 44% (0,58 vs 1,03 zł)
- **G12as:** Dla ogrzewania elektrycznego
- **G12w:** Weekend cała doba w niższej cenie

---

**Oblicz swój rachunek!** [Kalkulator ENEA](/kalkulatory/zaawansowany)`,
    osd: ['ENEA'],
    ctaButtons: [
      {
        text: 'Kalkulator ENEA',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'blue',
        icon: 'IconChartBar',
      },
      {
        text: 'Szczegóły taryf',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #8d: Zmiany taryf energetycznych 2026 - ENERGA (FEATURED, 2025-12-19)
  {
    id: 'zmiany-taryf-2026-energa',
    slug: 'zmiany-taryf-2026-energa',
    title: 'Taryfy ENERGA 2026 - Co się zmienia od 1 stycznia?',
    category: 'announcement',
    date: '2025-12-19T10:00:00Z',
    featured: true,
    icon: 'IconBolt',
    tags: ['taryfy', '2026', 'URE', 'opłaty', 'energia', 'zmiany', 'ENERGA'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '5 min',
    excerpt: 'Od 1 stycznia 2026 wchodzą w życie nowe taryfy URE dla ENERGA. Sprawdź aktualne ceny energii dla swojego dystrybutora!',
    content: `# Taryfy ENERGA 2026 - Co się zmienia od 1 stycznia?

Od **1 stycznia 2026** wchodzą w życie nowe taryfy zatwierdzone przez Urząd Regulacji Energetyki (URE) dla **ENERGA-OPERATOR**.

## ⚠️ Cel edukacyjny

**Kalkulator służy wyłącznie celom edukacyjnym.** Przed zmianą taryfy skonsultuj się z ENERGA.

## 📊 Ceny ENERGA 2026 (brutto)

| Taryfa | Cena | Szczegóły |
|--------|------|-----------|
| **G11** | 0,99 zł/kWh | Jednostrefowa |
| **G12** | Dzień: 1,04 zł · Noc: 0,59 zł | Dwustrefowa (06-22/22-06) |
| **G12w** | Robocze: 1,03 zł · Weekend: 0,78 zł | Weekendowa |
| **G12as** | Dzień: 1,04 zł · Noc: 0,76 zł | Antysmogowa |
| **G12r** | Dzień: 1,03 zł · Noc: 0,76 zł | Rozszerzona (21-07 + weekendy) |

*G12r to specyficzna taryfa ENERGA z dłuższą strefą nocną 21:00-07:00 + całe weekendy*

## 💪 Opłata mocowa 2026

| Roczne zużycie | 2026 netto |
|----------------|------------|
| < 500 kWh | 4,29 zł/mies. |
| 500-1200 kWh | 10,31 zł/mies. |
| 1200-2800 kWh | 17,18 zł/mies. |
| > 2800 kWh | 24,05 zł/mies. |

## 💡 Jak oszczędzać z ENERGA?

- **G12:** Noc tańsza o 43% (0,59 vs 1,04 zł)
- **G12r:** Dłuższa noc od 21:00! (tylko ENERGA)
- **G12as:** Dla pomp ciepła i ogrzewania elektrycznego

---

**Oblicz swój rachunek!** [Kalkulator ENERGA](/kalkulatory/zaawansowany)`,
    osd: ['ENERGA'],
    ctaButtons: [
      {
        text: 'Kalkulator ENERGA',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'blue',
        icon: 'IconChartBar',
      },
      {
        text: 'Szczegóły taryf',
        url: '/przewodnik/taryfy',
        variant: 'outline',
        color: 'blue',
        icon: 'IconTable',
      },
    ],
  },

  // News #7: Przewodnik Analiz (FEATURED, 2025-11-08)
  {
    id: 'przewodnik-analiz',
    slug: 'przewodnik-analiz',
    title: 'Przewodnik Analiz - 10 szczegółowych poradników oszczędnościowych',
    category: 'feature',
    date: '2025-11-08T10:00:00Z',
    featured: true,
    icon: 'IconReportAnalytics',
    tags: ['przewodnik', 'analizy', 'oszczędności', 'taryfy', 'poradnik'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt: 'Nowy Przewodnik Analiz! 10 szczegółowych artykułów dopasowanych do Twojego poziomu rachunków. Sprawdź co możesz zrobić, jeśli płacisz 100-200 zł, masz auto elektryczne lub pompę ciepła.',
    content: `# Przewodnik Analiz - Znajdź swój profil oszczędnościowy!

Dziękujemy za zaufanie! Wprowadziliśmy **Przewodnik Analiz** - 10 szczegółowych artykułów dopasowanych do różnych profili użytkowników energii.

## Co znajdziesz w Przewodniku?

### 📊 6 Analiz według przedziałów kosztów

Sprawdź, co możesz zrobić w zależności od wysokości rachunków:

- **Płacisz < 100 zł/miesiąc?** Minimalne zużycie (800-1200 kWh/rok)
- **100-200 zł/miesiąc** - Średnie zużycie (1500-2400 kWh/rok)
- **200-300 zł/miesiąc** - Podwyższone zużycie (2400-3600 kWh/rok)
- **300-500 zł/miesiąc** - Wysokie zużycie (3600-6000 kWh/rok)
- **500-700 zł/miesiąc** - Bardzo wysokie zużycie (6000-8400 kWh/rok)
- **700-1000 zł/miesiąc** - Ekstremalne zużycie (8400-12000 kWh/rok)

### 🎯 4 Analizy tematyczne

Specjalistyczne poradniki dla konkretnych sytuacji:

- **Auto elektryczne** - Jak tanio ładować EV w domu? Taryfa G12 vs G12w
- **Pompa ciepła** - Optymalizacja kosztów ogrzewania, taryfa G12as
- **Liczba osób w domu** - Jaki jest normalne zużycie dla 1-5 osób?
- **Ranking oszczędności** - Co naprawdę działa, a co to mit?

## Przykład: Płacisz 200-300 zł/miesiąc?

W tej analizie znajdziesz:

### 📈 Profil użytkownika
- Zużycie: 2400-3600 kWh/rok
- Typ mieszkania: 3-pokojowe, 60-80m²
- Liczba osób: 2-3 osoby
- Urządzenia: Standardowe AGD

### 💰 Potencjalne oszczędności
- **Zmiana na G12:** 400-800 zł/rok
- **Zmiana na G12w:** 500-1000 zł/rok
- **Optymalizacja zużycia:** 300-600 zł/rok

### ✅ Konkretne rekomendacje
- Kiedy zmiana taryfy **jest zalecana**
- Kiedy **nie warto** zmieniać
- Jak **monitorować zużycie**
- Jakie **urządzenia** zużywają najwięcej

### 🔗 Linki do kalkulatorów
Każdy artykuł zawiera bezpośrednie linki do:
- [Kalkulator podstawowy](/kalkulatory/podstawowy) - szybkie porównanie
- [Kalkulator zaawansowany](/kalkulatory/zaawansowany) - szczegółowa analiza godzinowa

## Przykład: Masz auto elektryczne?

W analizie dla właścicieli EV znajdziesz:

### 🚗 Koszty ładowania
- Jak tanio ładować w domu?
- Porównanie G11 vs G12 vs G12w
- Optymalne godziny ładowania

### 💡 Oszczędności
- **Taryfa G12:** Oszczędność 500-1000 zł/rok
- **Taryfa G12w:** Oszczędność 700-1500 zł/rok (ładowanie w weekendy)

### 📊 Rekomendacje
- Kiedy ładować (godziny nocne)
- Ile zużywa ładowanie (200-400 kWh/miesiąc)
- Czy warto instalować wallbox?

## Jak zacząć?

### Krok 1: Znajdź swój profil
Odwiedź [Przewodnik Analiz](/przewodnik/analizy) i znajdź artykuł dopasowany do Twojej sytuacji.

### Krok 2: Przeczytaj analizę
Każdy artykuł to 6-12 minut czytania z konkretnymi danymi i rekomendacjami.

### Krok 3: Użyj kalkulatora
Sprawdź dokładne oszczędności za pomocą naszych kalkulatorów:
- [Kalkulator podstawowy](/kalkulatory/podstawowy) - dla szybkiego porównania
- [Kalkulator zaawansowany](/kalkulatory/zaawansowany) - dla precyzyjnej symulacji

### Krok 4: Podejmij decyzję
Na podstawie analizy i wyników kalkulatora zdecyduj, czy zmiana taryfy jest dla Ciebie opłacalna.

## Stonowana komunikacja

Wszystkie analizy napisane są w **stonowanym, merytorycznym tonie**:
- ✅ Obiektywne dane i statystyki
- ✅ Zalecenia, nie nakazy
- ✅ Analiza "kiedy warto" i "kiedy nie warto"
- ❌ Brak presji i kategorycznych stwierdzeń

## Dla kogo?

### 👨‍👩‍👧‍👦 Gospodarstwa domowe
Sprawdź, jakie oszczędności są możliwe w Twojej sytuacji.

### 🚗 Właściciele aut elektrycznych
Dowiedz się, jak tanio ładować EV w domu.

### 🏠 Właściciele pomp ciepła
Optymalizuj koszty ogrzewania elektrycznego.

### 💡 Osoby planujące zmianę taryfy
Zrozum, czy zmiana jest dla Ciebie opłacalna.

---

**Chcesz znaleźć swój profil?** [Przejdź do Przewodnika Analiz](/przewodnik/analizy) już dziś!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Przejdź do Przewodnika',
        url: '/przewodnik/analizy',
        variant: 'filled',
        color: 'blue',
        icon: 'IconReportAnalytics',
      },
      {
        text: 'Wypróbuj kalkulator',
        url: '/kalkulatory/podstawowy',
        variant: 'outline',
        color: 'green',
        icon: 'IconCalculator',
      },
    ],
  },

  // News #2: Udostępnianie kalkulacji (FEATURED, 2025-11-07)
  {
    id: 'udostepnianie-kalkulacji',
    slug: 'udostepnianie-kalkulacji',
    title: 'Udostępniaj kalkulacje znajomym - łatwo i bezpiecznie',
    category: 'feature',
    date: '2025-11-07T14:00:00Z',
    featured: true,
    icon: 'IconShare',
    tags: ['udostępnianie', 'link', 'publiczny', 'sharing'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Nowa funkcja udostępniania! Teraz możesz podzielić się wynikami swojej kalkulacji przez unikalny link. Znajomi zobaczą Twoje porównanie taryf bez konieczności logowania.',
    content: `# Udostępniaj kalkulacje znajomym - łatwo i bezpiecznie

Wiemy, że często chcesz pokazać swoje wyniki znajomym lub rodzinie. Dlatego wprowadziliśmy **funkcję udostępniania kalkulacji**!

## Jak to działa?

### 1️⃣ Wypełnij kalkulator
Użyj [Kalkulatora Podstawowego](/kalkulatory/podstawowy) lub [Zaawansowanego](/kalkulatory/zaawansowany) i wypełnij swoje dane.

### 2️⃣ Kliknij "Udostępnij"
Pod wynikami znajdziesz przycisk **"Udostępnij kalkulację"**. Kliknij go, aby wygenerować unikalny link.

### 3️⃣ Skopiuj link
System wygeneruje bezpieczny link, np:
\`\`\`
https://taryfypradu.pl/shared/abc123def456
\`\`\`

### 4️⃣ Wyślij znajomym
Udostępnij link przez:
- Email
- WhatsApp / Messenger
- SMS
- Media społecznościowe

### 5️⃣ Znajomi widzą wyniki
Każdy, kto ma link, zobaczy:
- ✅ Twoje parametry (zużycie, strefa, profil)
- ✅ Porównanie taryf
- ✅ Wykresy kosztów
- ❌ **Brak danych osobowych** (email, imię, nazwisko)

## Bezpieczeństwo

### 🔒 Unikalny link
Każda kalkulacja ma unikalny, trudny do odgadnięcia identyfikator.

### 🚫 Brak danych wrażliwych
Udostępniamy tylko parametry kalkulacji, nigdy Twoich danych osobowych.

### ⏱️ Bez wygasania
Link działa bezterminowo - możesz wrócić do niego w przyszłości.

## Przykłady użycia

### Dla rodziny
*"Mamo, zobacz jak możesz zaoszczędzić 400 zł rocznie na taryfie G12!"*

### Dla znajomych
*"Kolego, sprawdź moją analizę pompy ciepła - może Ci się przyda"*

### Dla doradców energetycznych
*"Przygotowałem porównanie dla Pana - proszę kliknąć w link"*

## Dostępność

Funkcja udostępniania dostępna jest dla:
- ✅ **Kalkulatora Podstawowego** (bez logowania)
- ✅ **Kalkulatora Zaawansowanego** (dla zalogowanych)

---

**Chcesz pokazać swoje oszczędności?** Wypróbuj funkcję udostępniania już dziś!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Wypróbuj teraz',
        url: '/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Kalkulator zaawansowany',
        url: '/kalkulatory/zaawansowany',
        variant: 'outline',
        color: 'green',
        icon: 'IconChartLine',
      },
    ],
  },

  // News #5: Jak działa kalkulator (2025-11-04)
  {
    id: 'jak-dziala-kalkulator',
    slug: 'jak-dziala-kalkulator',
    title: 'Jak działa Kalkulator Zaawansowany? Poznaj szczegóły!',
    category: 'update',
    date: '2025-11-04T16:00:00Z',
    featured: false,
    icon: 'IconBook',
    tags: ['edukacja', 'kalkulator', 'wzorce', 'poradnik'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '4 min',
    excerpt: 'Chcesz dowiedzieć się, jak działa Kalkulator Zaawansowany? Przygotowaliśmy szczegółowy przewodnik wyjaśniający profile zużycia, strefy taryfowe i metodologię obliczeń.',
    content: `# Jak działa Kalkulator Zaawansowany? Poznaj szczegóły!

Dodaliśmy **dedykowaną stronę edukacyjną** wyjaśniającą, jak działa Kalkulator Zaawansowany i jak najlepiej wykorzystać profile zużycia energii.

## Co znajdziesz na stronie?

### 📚 Podstawy teorii
- Co to są **profile zużycia godzinowego**?
- Czym różnią się **strefy taryfowe**? (szczyt, poza-szczyt, dolina)
- Jak działa **taryfa dwustrefowa G12**?
- Jak działa **taryfa trzystrefowa G13**?

### 🎯 Praktyczne przykłady
- **Profil domowy standardowy** - dla większości gospodarstw
- **Profil z pompą ciepła** - nocne ogrzewanie
- **Profil z fotowoltaiką** - produkcja energii w dzień
- **Profil z samochodem elektrycznym** - ładowanie nocne

### 📊 Metodologia obliczeń
- Jak tworzymy **symulację 8760 godzin**?
- Dlaczego **dni robocze ≠ weekendy**?
- Jak liczymy **koszty roczne**?
- Skąd pochodzą **ceny energii**?

### ⚠️ Zastrzeżenia prawne
- Symulacje są **orientacyjne** (nie gwarancją kosztów)
- Ceny mogą się zmienić (taryfy aktualizowane co kwartał)
- Różnice indywidualne (zużycie może się różnić)

## Dla kogo?

### 👨‍👩‍👧‍👦 Użytkownicy indywidualni
Chcesz zrozumieć, dlaczego taryfa G12 może być dla Ciebie tańsza niż G11? Zajrzyj do naszego przewodnika!

### 🏢 Doradcy energetyczni
Pomagasz klientom wybierać taryfy? Nasze materiały edukacyjne pomogą wyjaśnić złożone zagadnienia.

### 🎓 Studenci i edukatorzy
Interesujesz się energetyką? Nasza strona wyjaśnia podstawy taryfikacji energii.

## Highlights

### 💡 Przykład: Pompa ciepła
Dowiedz się, jak skonfigurować profil dla pompy ciepła, która:
- Grzeje głównie w nocy (tania strefa)
- Akumuluje ciepło w zasobniku
- Minimalizuje koszty energii

**Efekt:** Oszczędności do 40% w porównaniu do G11!

### 🌞 Przykład: Fotowoltaika
Poznaj, jak modelować:
- Nadprodukcję energii w dzień (oddajesz do sieci)
- Pobór energii w nocy (kupujesz z sieci)
- Bilans roczny (prosument)

**Efekt:** Wybór optymalnej taryfy dla prosumenta!

## Jak zacząć?

Odwiedź naszą [stronę edukacyjną](/consumption-patterns) i:
1. Przeczytaj **podstawy teorii**
2. Zobacz **przykładowe profile**
3. Stwórz **własny profil**
4. Przetestuj w [Kalkulatorze Zaawansowanym](/kalkulatory/zaawansowany)

---

**Chcesz dowiedzieć się więcej?** [Przejdź do przewodnika](/consumption-patterns)!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Dowiedz się więcej',
        url: '/consumption-patterns',
        variant: 'filled',
        color: 'blue',
        icon: 'IconBook',
      },
      {
        text: 'Wypróbuj kalkulator',
        url: '/kalkulatory/zaawansowany',
        variant: 'outline',
        color: 'green',
        icon: 'IconChartLine',
      },
    ],
  },

  // News #1: Kalkulator Zaawansowany (FEATURED, 2025-11-03)
  {
    id: 'kalkulator-zaawansowany',
    slug: 'kalkulator-zaawansowany',
    title: 'Kalkulator Zaawansowany - Precyzyjna symulacja zużycia energii',
    category: 'feature',
    date: '2025-11-03T10:00:00Z',
    featured: true,
    icon: 'IconChartLine',
    tags: ['kalkulator', 'zaawansowany', 'profile', 'symulacja'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt: 'Wprowadziliśmy Kalkulator Zaawansowany z precyzyjnymi profilami zużycia energii! Teraz możesz zdefiniować osobne wzorce dla dni roboczych i weekendów, uzyskując realistyczną symulację rocznych kosztów energii z dokładnością co do godziny.',
    content: `# Kalkulator Zaawansowany - Precyzyjna symulacja zużycia energii

Dziękujemy za zaufanie! Z dumą przedstawiamy **Kalkulator Zaawansowany** - nowe narzędzie, które pozwala na precyzyjne planowanie kosztów energii elektrycznej.

## Co nowego?

### 📊 Profile zużycia 24-godzinne
Stwórz własny profil zużycia energii na każdą godzinę doby:
- **Dni robocze** (poniedziałek-piątek): Dostosuj do swojego stylu życia
- **Weekendy** (sobota-niedziela): Osobny profil dla dni wolnych
- **Suma kontrolna**: System automatycznie dba, aby suma godzinowa wynosiła 100%

### 🎯 Realistyczna symulacja roczna
Kalkulator tworzy symulację **8760 godzin** (365 dni × 24 godziny), uwzględniając:
- Różnice między dniami roboczymi a weekendami
- Strefy taryfowe (szczyt, poza-szczyt, dolina)
- Opłaty stałe i zmienne
- Miesięczne rozbicia kosztów

### 💾 Zapisuj i wczytuj kalkulacje
- Zapisz swoje profile jako szablony
- Wczytaj zapisane kalkulacje w dowolnym momencie
- Porównuj różne scenariusze

### 📥 Eksport do CSV
Wyeksportuj pełną symulację 8760 godzin do pliku CSV i analizuj w Excel lub Google Sheets.

## Przykład: Pompa ciepła

Jeśli ogrzewasz dom pompą ciepła, możesz:
1. Ustawić większe zużycie w godzinach nocnych (tańsza energia w dolinie)
2. Zwiększyć zużycie w miesiącach zimowych (październik-marzec)
3. Porównać koszty różnych taryf (G12, G12w, G12as)

**Oszczędności:** Nawet 30-40% w porównaniu do taryfy G11!

## Jakość i dokładność

Kalkulator został przetestowany **82 kompleksowymi testami** zapewniającymi dokładność obliczeń. Możesz nam zaufać!

## Jak zacząć?

1. Zaloguj się na swoje konto (lub [zarejestruj się za darmo](/signup))
2. Przejdź do [Kalkulatora Zaawansowanego](/kalkulatory/zaawansowany)
3. Wypełnij dane podstawowe (zużycie roczne, strefa)
4. Stwórz własne profile godzinowe
5. Porównaj wyniki dla różnych taryf!

---

**Gotowy na precyzyjną analizę?** Wypróbuj Kalkulator Zaawansowany już dziś!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Wypróbuj kalkulator',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'green',
        icon: 'IconChartLine',
      },
      {
        text: 'Dowiedz się więcej',
        url: '/consumption-patterns',
        variant: 'outline',
        color: 'green',
        icon: 'IconInfoCircle',
      },
    ],
  },

  // News #6a: Transparentność danych - TAURON (2025-10-29)
  {
    id: 'transparentnosc-danych-tauron',
    slug: 'transparentnosc-danych-tauron',
    title: 'Transparentność przede wszystkim - Skąd mamy dane TAURON?',
    category: 'update',
    date: '2025-10-29T13:00:00Z',
    featured: false,
    icon: 'IconShieldCheck',
    tags: ['transparentność', 'dane', 'ceny', 'taryfy', 'TAURON'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Jesteśmy transparentni! Dowiedz się, skąd pochodzą nasze dane cenowe TAURON, jak je weryfikujemy i dlaczego możesz nam zaufać.',
    content: `# Transparentność przede wszystkim - Skąd mamy dane TAURON? 🔍

Wierzymy w **pełną transparentność**. Dlatego chcemy wyjaśnić, skąd pochodzą nasze dane cenowe i jak je weryfikujemy.

## ⚠️ Cel edukacyjny

**Kalkulator TaryfyPrądu.pl służy wyłącznie celom edukacyjnym i informacyjnym.** Nie stanowi oferty handlowej ani doradztwa finansowego. Przed podjęciem decyzji o zmianie taryfy skonsultuj się ze swoim sprzedawcą energii.

## Źródła danych TAURON

### 📋 Taryfy TAURON® Dystrybucja
Ceny energii i dystrybucji pobieramy z **oficjalnych taryf TAURON®**:
- Pobieramy z [www.tauron.pl](https://www.tauron.pl/taryfy)
- Aktualizujemy co kwartał (lub częściej, jeśli taryfy się zmieniają)
- Weryfikujemy ręcznie przed publikacją

### 🏛️ Urząd Regulacji Energetyki (URE)
Sprawdzamy zgodność z taryfami zatwierdzonymi przez URE:
- Porównujemy z [www.ure.gov.pl](https://www.ure.gov.pl)
- Weryfikujemy daty obowiązywania
- Sprawdzamy poprawność strefy geograficznej

## Zasięg TAURON Dystrybucja

**TAURON** obsługuje ~5,7 mln klientów w regionach:
- dolnośląskie, małopolskie, opolskie, śląskie
- część świętokrzyskiego, podkarpackiego, łódzkiego

## Dostępne taryfy TAURON

| Taryfa | Opis | Cena 2026 (brutto) |
|--------|------|------------------|
| **G11** | Jednostrefowa | 0,89 zł/kWh |
| **G12** | Dwustrefowa | Dzień: 1,02 zł · Noc: 0,52 zł |
| **G12w** | Weekendowa | Robocze: 1,00 zł · Weekend: 0,58 zł |
| **G12as** | Antysmogowa | Dzień: 0,90 zł · Noc: 0,58 zł |
| **G13** | Trzystrefowa | Szczyt: 1,01 zł · Poza: 0,58 zł |
| **G13s** | Dynamiczna OZE | Solar: 0,58 zł · Inne: 0,92 zł |
| **G14** | Dynamiczna RCE | Zmienna (~0,90 zł średnio) |

## Jak weryfikujemy dane?

### ✅ Proces 3-etapowy
1. **Pobieranie** - Automatyczne pobieranie taryf z oficjalnych źródeł
2. **Weryfikacja** - Ręczna kontrola przez zespół
3. **Publikacja** - Aktualizacja cen w kalkulatorze

### ⚠️ Zastrzeżenia
- Ceny mogą się zmienić (taryfy aktualizowane co kwartał)
- Indywidualne umowy mogą mieć inne ceny
- Promocje i rabaty nie są uwzględniane
- **Kalkulator służy celom edukacyjnym**

## Znalazłeś błąd?

Jeśli zauważysz błąd w cenach, [zgłoś go tutaj](/contact). Sprawdzimy i poprawimy!

---

**Chcesz zobaczyć nasze dane?** [Sprawdź taryfy](/przewodnik/taryfy)`,
    osd: ['TAURON'],
    ctaButtons: [
      {
        text: 'Zobacz taryfy TAURON',
        url: '/przewodnik/taryfy',
        variant: 'filled',
        color: 'blue',
        icon: 'IconTable',
      },
      {
        text: 'Kalkulator TAURON',
        url: '/kalkulatory/podstawowy',
        variant: 'outline',
        color: 'green',
        icon: 'IconCalculator',
      },
    ],
  },

  // News #6b: Transparentność danych - PGE (2025-10-29)
  {
    id: 'transparentnosc-danych-pge',
    slug: 'transparentnosc-danych-pge',
    title: 'Transparentność przede wszystkim - Skąd mamy dane PGE?',
    category: 'update',
    date: '2025-10-29T13:00:00Z',
    featured: false,
    icon: 'IconShieldCheck',
    tags: ['transparentność', 'dane', 'ceny', 'taryfy', 'PGE'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Jesteśmy transparentni! Dowiedz się, skąd pochodzą nasze dane cenowe PGE, jak je weryfikujemy i dlaczego możesz nam zaufać.',
    content: `# Transparentność przede wszystkim - Skąd mamy dane PGE? 🔍

Wierzymy w **pełną transparentność**. Dlatego chcemy wyjaśnić, skąd pochodzą nasze dane cenowe i jak je weryfikujemy.

## ⚠️ Cel edukacyjny

**Kalkulator TaryfyPrądu.pl służy wyłącznie celom edukacyjnym i informacyjnym.** Nie stanowi oferty handlowej ani doradztwa finansowego. Przed podjęciem decyzji o zmianie taryfy skonsultuj się ze swoim sprzedawcą energii.

## Źródła danych PGE

### 📋 Taryfy PGE® Dystrybucja
Ceny energii i dystrybucji pobieramy z **oficjalnych taryf PGE®**:
- Pobieramy z [www.gkpge.pl](https://www.gkpge.pl/dla-domu/taryfy)
- Aktualizujemy co kwartał (lub częściej, jeśli taryfy się zmieniają)
- Weryfikujemy ręcznie przed publikacją

### 🏛️ Urząd Regulacji Energetyki (URE)
Sprawdzamy zgodność z taryfami zatwierdzonymi przez URE:
- Porównujemy z [www.ure.gov.pl](https://www.ure.gov.pl)
- Weryfikujemy daty obowiązywania

## Zasięg PGE Dystrybucja

**PGE** obsługuje ~5,5 mln klientów w regionach:
- mazowieckie, łódzkie, lubelskie, podlaskie
- część podkarpackiego, świętokrzyskiego

## Dostępne taryfy PGE

| Taryfa | Opis | Cena 2026 (brutto) |
|--------|------|------------------|
| **G11** | Jednostrefowa | 1,00 zł/kWh |
| **G12** | Dwustrefowa | Dzień: 1,04 zł · Noc: 0,59 zł |
| **G12w** | Weekendowa | Robocze: 1,03 zł · Weekend: 0,78 zł |
| **G12n** | Nocna (PGE) | Dzień: 1,04 zł · Noc: 0,62 zł |

*G12n to specyficzna taryfa PGE z krótką strefą nocną 01:00-05:00 + całe weekendy*

## Jak weryfikujemy dane?

### ✅ Proces 3-etapowy
1. **Pobieranie** - Automatyczne pobieranie taryf z oficjalnych źródeł
2. **Weryfikacja** - Ręczna kontrola przez zespół
3. **Publikacja** - Aktualizacja cen w kalkulatorze

### ⚠️ Zastrzeżenia
- Ceny mogą się zmienić (taryfy aktualizowane co kwartał)
- Indywidualne umowy mogą mieć inne ceny
- Promocje i rabaty nie są uwzględniane
- **Kalkulator służy celom edukacyjnym**

## Znalazłeś błąd?

Jeśli zauważysz błąd w cenach, [zgłoś go tutaj](/contact). Sprawdzimy i poprawimy!

---

**Chcesz zobaczyć nasze dane?** [Sprawdź taryfy](/przewodnik/taryfy)`,
    osd: ['PGE'],
    ctaButtons: [
      {
        text: 'Zobacz taryfy PGE',
        url: '/przewodnik/taryfy',
        variant: 'filled',
        color: 'blue',
        icon: 'IconTable',
      },
      {
        text: 'Kalkulator PGE',
        url: '/kalkulatory/podstawowy',
        variant: 'outline',
        color: 'green',
        icon: 'IconCalculator',
      },
    ],
  },

  // News #6c: Transparentność danych - ENEA (2025-10-29)
  {
    id: 'transparentnosc-danych-enea',
    slug: 'transparentnosc-danych-enea',
    title: 'Transparentność przede wszystkim - Skąd mamy dane ENEA?',
    category: 'update',
    date: '2025-10-29T13:00:00Z',
    featured: false,
    icon: 'IconShieldCheck',
    tags: ['transparentność', 'dane', 'ceny', 'taryfy', 'ENEA'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Jesteśmy transparentni! Dowiedz się, skąd pochodzą nasze dane cenowe ENEA, jak je weryfikujemy i dlaczego możesz nam zaufać.',
    content: `# Transparentność przede wszystkim - Skąd mamy dane ENEA? 🔍

Wierzymy w **pełną transparentność**. Dlatego chcemy wyjaśnić, skąd pochodzą nasze dane cenowe i jak je weryfikujemy.

## ⚠️ Cel edukacyjny

**Kalkulator TaryfyPrądu.pl służy wyłącznie celom edukacyjnym i informacyjnym.** Nie stanowi oferty handlowej ani doradztwa finansowego. Przed podjęciem decyzji o zmianie taryfy skonsultuj się ze swoim sprzedawcą energii.

## Źródła danych ENEA

### 📋 Taryfy ENEA® Operator
Ceny energii i dystrybucji pobieramy z **oficjalnych taryf ENEA®**:
- Pobieramy z [www.enea.pl](https://www.enea.pl/taryfy)
- Aktualizujemy co kwartał (lub częściej, jeśli taryfy się zmieniają)
- Weryfikujemy ręcznie przed publikacją

### 🏛️ Urząd Regulacji Energetyki (URE)
Sprawdzamy zgodność z taryfami zatwierdzonymi przez URE:
- Porównujemy z [www.ure.gov.pl](https://www.ure.gov.pl)
- Weryfikujemy daty obowiązywania

## Zasięg ENEA Operator

**ENEA** obsługuje ~2,6 mln klientów w regionach:
- wielkopolskie, zachodniopomorskie, kujawsko-pomorskie, lubuskie
- część dolnośląskiego, pomorskiego

## Dostępne taryfy ENEA

| Taryfa | Opis | Cena 2026 (brutto) |
|--------|------|------------------|
| **G11** | Jednostrefowa | 0,99 zł/kWh |
| **G12** | Dwustrefowa | Dzień: 1,03 zł · Noc: 0,58 zł |
| **G12w** | Weekendowa | Robocze: 1,02 zł · Weekend: 0,78 zł |
| **G12as** | Antysmogowa | Dzień: 1,03 zł · Noc: 0,76 zł |

## Jak weryfikujemy dane?

### ✅ Proces 3-etapowy
1. **Pobieranie** - Automatyczne pobieranie taryf z oficjalnych źródeł
2. **Weryfikacja** - Ręczna kontrola przez zespół
3. **Publikacja** - Aktualizacja cen w kalkulatorze

### ⚠️ Zastrzeżenia
- Ceny mogą się zmienić (taryfy aktualizowane co kwartał)
- Indywidualne umowy mogą mieć inne ceny
- Promocje i rabaty nie są uwzględniane
- **Kalkulator służy celom edukacyjnym**

## Znalazłeś błąd?

Jeśli zauważysz błąd w cenach, [zgłoś go tutaj](/contact). Sprawdzimy i poprawimy!

---

**Chcesz zobaczyć nasze dane?** [Sprawdź taryfy](/przewodnik/taryfy)`,
    osd: ['ENEA'],
    ctaButtons: [
      {
        text: 'Zobacz taryfy ENEA',
        url: '/przewodnik/taryfy',
        variant: 'filled',
        color: 'blue',
        icon: 'IconTable',
      },
      {
        text: 'Kalkulator ENEA',
        url: '/kalkulatory/podstawowy',
        variant: 'outline',
        color: 'green',
        icon: 'IconCalculator',
      },
    ],
  },

  // News #6d: Transparentność danych - ENERGA (2025-10-29)
  {
    id: 'transparentnosc-danych-energa',
    slug: 'transparentnosc-danych-energa',
    title: 'Transparentność przede wszystkim - Skąd mamy dane ENERGA?',
    category: 'update',
    date: '2025-10-29T13:00:00Z',
    featured: false,
    icon: 'IconShieldCheck',
    tags: ['transparentność', 'dane', 'ceny', 'taryfy', 'ENERGA'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Jesteśmy transparentni! Dowiedz się, skąd pochodzą nasze dane cenowe ENERGA, jak je weryfikujemy i dlaczego możesz nam zaufać.',
    content: `# Transparentność przede wszystkim - Skąd mamy dane ENERGA? 🔍

Wierzymy w **pełną transparentność**. Dlatego chcemy wyjaśnić, skąd pochodzą nasze dane cenowe i jak je weryfikujemy.

## ⚠️ Cel edukacyjny

**Kalkulator TaryfyPrądu.pl służy wyłącznie celom edukacyjnym i informacyjnym.** Nie stanowi oferty handlowej ani doradztwa finansowego. Przed podjęciem decyzji o zmianie taryfy skonsultuj się ze swoim sprzedawcą energii.

## Źródła danych ENERGA

### 📋 Taryfy ENERGA® Operator
Ceny energii i dystrybucji pobieramy z **oficjalnych taryf ENERGA®**:
- Pobieramy z [www.energa.pl](https://www.energa.pl/taryfy)
- Aktualizujemy co kwartał (lub częściej, jeśli taryfy się zmieniają)
- Weryfikujemy ręcznie przed publikacją

### 🏛️ Urząd Regulacji Energetyki (URE)
Sprawdzamy zgodność z taryfami zatwierdzonymi przez URE:
- Porównujemy z [www.ure.gov.pl](https://www.ure.gov.pl)
- Weryfikujemy daty obowiązywania

## Zasięg ENERGA Operator

**ENERGA** obsługuje ~3,2 mln klientów w regionach:
- pomorskie, warmińsko-mazurskie
- część kujawsko-pomorskiego, mazowieckiego, wielkopolskiego

## Dostępne taryfy ENERGA

| Taryfa | Opis | Cena 2026 (brutto) |
|--------|------|------------------|
| **G11** | Jednostrefowa | 0,99 zł/kWh |
| **G12** | Dwustrefowa | Dzień: 1,04 zł · Noc: 0,59 zł |
| **G12w** | Weekendowa | Robocze: 1,03 zł · Weekend: 0,78 zł |
| **G12as** | Antysmogowa | Dzień: 1,04 zł · Noc: 0,76 zł |
| **G12r** | Rozszerzona (Energa) | Dzień: 1,03 zł · Noc: 0,76 zł |

*G12r to specyficzna taryfa ENERGA z dłuższą strefą nocną 21:00-07:00 + całe weekendy*

## Jak weryfikujemy dane?

### ✅ Proces 3-etapowy
1. **Pobieranie** - Automatyczne pobieranie taryf z oficjalnych źródeł
2. **Weryfikacja** - Ręczna kontrola przez zespół
3. **Publikacja** - Aktualizacja cen w kalkulatorze

### ⚠️ Zastrzeżenia
- Ceny mogą się zmienić (taryfy aktualizowane co kwartał)
- Indywidualne umowy mogą mieć inne ceny
- Promocje i rabaty nie są uwzględniane
- **Kalkulator służy celom edukacyjnym**

## Znalazłeś błąd?

Jeśli zauważysz błąd w cenach, [zgłoś go tutaj](/contact). Sprawdzimy i poprawimy!

---

**Chcesz zobaczyć nasze dane?** [Sprawdź taryfy](/przewodnik/taryfy)`,
    osd: ['ENERGA'],
    ctaButtons: [
      {
        text: 'Zobacz taryfy ENERGA',
        url: '/przewodnik/taryfy',
        variant: 'filled',
        color: 'blue',
        icon: 'IconTable',
      },
      {
        text: 'Kalkulator ENERGA',
        url: '/kalkulatory/podstawowy',
        variant: 'outline',
        color: 'green',
        icon: 'IconCalculator',
      },
    ],
  },

  // News #4: Eksport CSV (2025-10-28)
  {
    id: 'eksport-csv',
    slug: 'eksport-csv',
    title: 'Eksport danych do CSV - Analizuj w Excel!',
    category: 'feature',
    date: '2025-10-28T11:00:00Z',
    featured: false,
    icon: 'IconFileDownload',
    tags: ['eksport', 'csv', 'excel', 'analiza'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '2 min',
    excerpt: 'Nowa funkcja eksportu CSV! Pobierz pełną symulację 8760 godzin i analizuj dane w Excel, Google Sheets lub innych narzędziach.',
    content: `# Eksport danych do CSV - Analizuj w Excel! 📥

Wprowadziliśmy funkcję **eksportu danych do pliku CSV**! Teraz możesz pobrać pełną symulację zużycia energii i analizować ją w swoich ulubionych narzędziach.

## Co otrzymujesz?

### 📊 Pełna symulacja 8760 godzin
Plik CSV zawiera:
- **Data i godzina** każdego punktu pomiarowego
- **Zużycie energii** (kWh) dla każdej godziny
- **Strefa taryfowa** (szczyt, poza-szczyt, dolina)
- **Cena jednostkowa** (zł/kWh) dla każdej strefy
- **Koszt godzinowy** (zł)
- **Typ dnia** (dzień roboczy, weekend)

### 📈 Gotowe do analizy
Plik jest w formacie CSV (rozdzielany przecinkami), kompatybilny z:
- **Microsoft Excel**
- **Google Sheets**
- **LibreOffice Calc**
- **Python / Pandas**
- **R / tidyverse**
- Inne narzędzia do analizy danych

## Jak eksportować?

### Krok 1: Użyj Kalkulatora Zaawansowanego
Przejdź do [Kalkulatora Zaawansowanego](/kalkulatory/zaawansowany) i wypełnij swoje dane.

### Krok 2: Kliknij "Eksportuj CSV"
Pod wynikami znajdziesz przycisk **"Eksportuj do CSV"**.

### Krok 3: Zapisz plik
Plik \`symulacja_8760h.csv\` zostanie automatycznie pobrany na Twój komputer.

### Krok 4: Otwórz w Excel
Kliknij dwukrotnie plik lub otwórz go w swoim ulubionym narzędziu.

## Przykłady analizy

### 📉 Wykres zużycia w czasie
Stwórz wykres liniowy pokazujący zużycie energii przez cały rok.

### 💰 Analiza kosztów miesięcznych
Użyj tabeli przestawnej (pivot table) do podsumowania kosztów według miesięcy.

### ⚡ Identyfikacja szczytów
Znajdź godziny i dni o najwyższym zużyciu energii.

### 🔍 Porównanie scenariuszy
Eksportuj kilka scenariuszy i porównaj je obok siebie.

## Dostępność

Eksport CSV dostępny jest dla:
- ✅ **Zalogowanych użytkowników** (darmowe konto)
- ✅ **Kalkulator Zaawansowany**

Nie masz konta? [Zarejestruj się za darmo](/signup) w 30 sekund!

## Format pliku

Przykład struktury CSV:
\`\`\`csv
Data,Godzina,Dzień tygodnia,Zużycie (kWh),Strefa,Cena (zł/kWh),Koszt (zł)
2025-01-01,00:00,Środa,0.5,noc,0.3500,0.1750
2025-01-01,01:00,Środa,0.5,noc,0.3500,0.1750
2025-01-01,02:00,Środa,0.4,noc,0.3500,0.1400
...
\`\`\`

---

**Chcesz analizować swoje dane?** [Wypróbuj eksport CSV](/kalkulatory/zaawansowany) już dziś!`,
    osd: ['ALL'],
    ctaButtons: [
      {
        text: 'Wypróbuj eksport',
        url: '/kalkulatory/zaawansowany',
        variant: 'filled',
        color: 'green',
        icon: 'IconChartLine',
      },
      {
        text: 'Zarejestruj się',
        url: '/signup',
        variant: 'outline',
        color: 'green',
        icon: 'IconUserPlus',
      },
    ],
  },

  // News #3: Start platformy (2025-10-26)
  {
    id: 'start-platformy',
    slug: 'start-platformy',
    title: 'Witamy w TaryfyPrądu.pl - Porównuj taryfy i oszczędzaj!',
    category: 'announcement',
    date: '2025-10-26T09:00:00Z',
    featured: false,
    icon: 'IconRocket',
    tags: ['start', 'platforma', 'taryfy', 'kalkulator'],
    author: 'Zespół TaryfyPrądu.pl',
    readTime: '3 min',
    excerpt: 'Witamy na TaryfyPrądu.pl! Stworzyliśmy platformę, która pomaga porównać taryfy energetyczne Tauron i wybrać najtańszą opcję. 100% darmowa, bez ukrytych opłat.',
    content: `# Witamy w TaryfyPrądu.pl! 🎉

Dziękujemy za odwiedzenie naszej platformy! Stworzyliśmy TaryfyPrądu.pl, aby pomóc Ci znaleźć najtańszą taryfę energetyczną.

## Co oferujemy?

### 📊 Kalkulator Podstawowy
Porównaj **6 najpopularniejszych taryf TAURON®**:
- **G11** - taryfa jednostrefowa (jedna cena przez całą dobę)
- **G12** - taryfa dwustrefowa (dzień/noc)
- **G12w** - taryfa dwustrefowa weekendowa
- **G12as** - taryfa dwustrefowa (dzień/noc z wyłącznikiem)
- **G13** - taryfa trzystrefowa (szczyt, poza-szczyt, noc)
- **G13s** - taryfa trzystrefowa (szczyt, dolina, pozostałe)

### 📈 Wizualizacje
- **Wykresy słupkowe** - porównanie kosztów rocznych
- **Wykresy kołowe** - podział kosztów na opłaty stałe i zmienne
- **Tabele szczegółowe** - dokładne rozbicie kosztów

### 💾 Zapisywanie kalkulacji
Zarejestruj **darmowe konto** i:
- Zapisz nieograniczoną liczbę kalkulacji
- Wróć do nich w dowolnym momencie
- Porównuj różne scenariusze

### 🔒 Bezpieczeństwo i prywatność
- **Zgodność z GDPR** - Twoje dane są chronione
- **Cookie Consent** z Google Consent Mode v2
- **SSL/TLS** - szyfrowane połączenie
- **Brak spamu** - wysyłamy tylko ważne informacje

## Dlaczego TaryfyPrądu.pl?

### ✅ 100% darmowa
Wszystkie funkcje dostępne za darmo. Bez ukrytych opłat.

### ✅ Aktualne ceny
Ceny aktualizowane zgodnie z taryfami Tauron.

### ✅ Transparentność
Pokazujemy źródła danych i metodologię obliczeń.

### ✅ Bez rejestracji sprzedawców
Nie udostępniamy Twoich danych sprzedawcom energii.

## Jak zacząć?

### Krok 1: Przejdź do kalkulatora
Otwórz [Kalkulator Podstawowy](/kalkulatory/podstawowy)

### Krok 2: Podaj dane
- Roczne zużycie energii (kWh)
- Strefa geograficzna (1, 2, 2m, 3, 3m)
- Obecna taryfa (opcjonalnie)

### Krok 3: Zobacz wyniki
- Porównaj koszty 6 taryf
- Zobacz, ile możesz zaoszczędzić
- Wybierz najtańszą opcję

### Krok 4: Zarejestruj się (opcjonalnie)
[Załóż darmowe konto](/signup) i zapisz swoją kalkulację.

## Co planujemy w przyszłości?

- 🔔 **Powiadomienia** o zmianach cen taryf
- 🏢 **Więcej dystrybutorów** (PGE®, ENEA®, ENERGA®)
- 💡 **Porady oszczędnościowe** - jak zmniejszyć rachunki
- 📄 **Analiza faktur** - automatyczna weryfikacja opłat
- 🌞 **Fotowoltaika** - kalkulatory dla prosumentów

## Pytania?

Odwiedź naszą [stronę FAQ](/faq) lub [skontaktuj się z nami](/contact).

---

**Gotowy na oszczędności?** [Rozpocznij porównywanie taryf](/kalkulatory/podstawowy) już dziś!`,
    osd: ['TAURON'],
    ctaButtons: [
      {
        text: 'Rozpocznij porównywanie',
        url: '/kalkulatory/podstawowy',
        variant: 'filled',
        color: 'green',
        icon: 'IconCalculator',
      },
      {
        text: 'Zarejestruj się za darmo',
        url: '/signup',
        variant: 'light',
        color: 'green',
        icon: 'IconUserPlus',
      },
    ],
  },
];

/**
 * Get recent news items (sorted by date, newest first)
 * @param limit - Maximum number of items to return
 */
export function getRecentNews(limit: number = 3): NewsItem[] {
  return NEWS_ITEMS.slice(0, limit);
}

/**
 * Get featured news items
 */
export function getFeaturedNews(): NewsItem[] {
  return NEWS_ITEMS.filter((item) => item.featured);
}

/**
 * Get news by category
 * @param category - News category to filter by
 */
export function getNewsByCategory(category: NewsCategory): NewsItem[] {
  return NEWS_ITEMS.filter((item) => item.category === category);
}

/**
 * Get news by slug
 * @param slug - News slug to find
 */
export function getNewsBySlug(slug: string): NewsItem | undefined {
  return NEWS_ITEMS.find((item) => item.slug === slug);
}

/**
 * Get news by ID
 * @param id - News ID to find
 */
export function getNewsById(id: string): NewsItem | undefined {
  return NEWS_ITEMS.find((item) => item.id === id);
}
